import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, OfflineError } from "./api";

/**
 * Loads something from the API and reloads it whenever `deps` change.
 *
 * Every screen takes the app's `revision` as a dependency, so a realtime event re-reads the truth
 * rather than patching state in place. That is design §5.3's rule where the UI can see it: an event
 * says something changed, and the only honest response is to go and look.
 *
 * Keeps the previous value visible while reloading. A phone re-reading after an event should not blink
 * its content away — the family is looking at a bracket, not a loading state.
 *
 * **`update` is the exception to "go and look", and it is there for a measured reason.** Re-reading is
 * the right default when an event only says *that* something changed. But a write whose own response
 * carries the new value already knows the answer, and going back to ask again is both slower and, for
 * the garage, expensive: listing it mints a CloudFront signature per image, so a full re-read after
 * every class change got slower with every truck the family owned. When a mutation returns the updated
 * object, splice it in with `update` and skip the round trip entirely.
 */
export function useLoad<T>(
  load: () => Promise<T>,
  deps: unknown[],
  onError?: (message: string) => void,
): {
  data: T | null;
  loading: boolean;
  reload: () => void;
  update: (change: (current: T) => T) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const inFlight = useRef<{ cancelled: boolean } | null>(null);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const update = useCallback((change: (current: T) => T) => {
    // A local edit outranks a read that was already in the air. Without this, a poll tick or a
    // realtime re-read that started before the edit lands after it and quietly reverts it on screen —
    // the value is correct again a few seconds later, which is exactly the kind of flicker that reads
    // as "the app lost my change".
    if (inFlight.current) inFlight.current.cancelled = true;
    setLoading(false);
    setData((current) => (current === null ? current : change(current)));
  }, []);

  useEffect(() => {
    const token = { cancelled: false };
    inFlight.current = token;
    setLoading(true);
    load()
      .then((value) => {
        if (!token.cancelled) setData(value);
      })
      .catch((cause: unknown) => {
        if (token.cancelled) return;
        // Offline is not worth interrupting anyone over — the next event or tab switch retries, and
        // whatever is already on screen is still the last thing the server said.
        if (cause instanceof OfflineError) return;
        const message =
          cause instanceof ApiError || cause instanceof Error ? cause.message : "Something went wrong.";
        onError?.(message);
      })
      .finally(() => {
        if (!token.cancelled) setLoading(false);
      });
    return () => {
      token.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, reload, update };
}

/**
 * Runs a write, surfaces its error, and reloads on success.
 *
 * `busy` is true while *any* write is in flight, which is the right answer for a screen with one
 * button and the wrong one for the garage, where it disabled every row's controls because somebody had
 * renamed one truck. Pass a `key` — a vehicle id, usually — and ask `isBusy(key)` instead. Callers that
 * do not care keep working unchanged.
 */
export function useAction(onError?: (message: string) => void) {
  const [pending, setPending] = useState<string[]>([]);

  const run = useCallback(
    async (work: () => Promise<unknown>, after?: () => void, key = "") => {
      setPending((current) => [...current, key]);
      try {
        await work();
        after?.();
      } catch (cause) {
        const message =
          cause instanceof ApiError || cause instanceof Error ? cause.message : "Something went wrong.";
        onError?.(message);
      } finally {
        setPending((current) => {
          const at = current.indexOf(key);
          return at === -1 ? current : [...current.slice(0, at), ...current.slice(at + 1)];
        });
      }
    },
    [onError],
  );

  const isBusy = useCallback((key: string) => pending.includes(key), [pending]);

  return { busy: pending.length > 0, isBusy, run };
}
